// "Who represents me?" (brief; README section 4).
//
// WHAT IS KEPT is the district and nothing else, in this browser's localStorage: the state and
// each Congress's seat. Never the address, not even the Census Bureau's tidied-up copy of it,
// which comes back with the answer and is shown only until the page changes. The brief: "The
// only thing remembered is a visitor's chosen district, kept in their own browser and never
// sent to the server." Nothing reads this key but this file.
//
// THE LOOKUP goes to this site's own server by POST, never with the address in a web address,
// and the server passes it to the Census Bureau's geocoder the same way (server/srweb/district.py).
//
// A SEAT IS PER CONGRESS. District lines change between Congresses (Texas's did for 2027), so
// the server asks each Congress's own lines, and a term is pinned with the seat for ITS lines.
// A term whose lines could not be asked shows its state's senators and says why no
// representative is pinned, rather than borrowing another Congress's seat.

import { locate, type DistrictMap } from './locate';
import { STATES } from './components/Chrome';

/** Why a term has no seat for a state its map leaves out: the server's address lookup says the same
 *  (server/srweb/district.py UNAVAILABLE), so the two ways of finding a district agree. */
export function unavailableWhy(state: string): string {
  return `${STATES[state] ?? state}’s district lines for this Congress are not in the Census Bureau’s files`;
}

const KEY = 'sr-district';

export interface District {
  v: 1;
  state: string;                           // "TX"
  senators: boolean;                       // false for DC and the territories
  seats: Record<string, string | null>;    // congress -> "TX-37", "AK-AL", or null
  missing?: Record<string, string>;        // congress -> why its seat is not known
}

export interface Choice extends Omit<District, 'v'> {
  matched: string;                          // the Census's tidied address, or the seat found from a position
  kind?: 'seat' | 'delegate' | 'none';      // from a position only: 'none' is water no district covers
}

export type Lookup =
  | ({ status: 'match' } & Choice)
  | { status: 'choose'; choices: Choice[] }
  | { status: 'none' }
  | { status: 'failed'; why: string };

function valid(d: unknown): d is District {
  const x = d as District;
  return !!x && x.v === 1 && typeof x.state === 'string' && /^[A-Z]{2}$/.test(x.state)
    && typeof x.seats === 'object' && x.seats !== null;
}

// Storage can be switched off, full, or refused in a private window; the lookup still works
// for the page on screen, it is simply not remembered.
export function loadDistrict(): District | null {
  try {
    const d = JSON.parse(localStorage.getItem(KEY) || 'null');
    return valid(d) ? d : null;
  } catch {
    return null;
  }
}

export function keep(c: Choice): District {
  const d: District = { v: 1, state: c.state, senators: c.senators, seats: c.seats, ...(c.missing ? { missing: c.missing } : {}) };
  try { localStorage.setItem(KEY, JSON.stringify(d)); } catch { /* not remembered; still shown */ }
  return d;
}

export function forget(): void {
  try { localStorage.removeItem(KEY); } catch { /* nothing to forget */ }
}

export async function lookUp(address: string): Promise<Lookup> {
  let r: Response;
  try {
    r = await fetch('/api/district', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ address }),
    });
  } catch {
    return { status: 'failed', why: 'the site’s server could not be reached' };
  }
  let body: { status?: string; why?: string } & Record<string, unknown> = {};
  try { body = await r.json(); } catch { /* reported below by status */ }
  if (r.ok && (body.status === 'match' || body.status === 'choose' || body.status === 'none')) return body as Lookup;
  return { status: 'failed', why: body.why || `the site’s server answered ${r.status}` };
}

// ---- "Use my location" ---------------------------------------------------------------------
//
// The browser asks the visitor's permission, then finds the district from the whole-country map
// (locate.ts). THE POSITION IS HELD ONLY IN THIS FUNCTION'S VARIABLES: it is not sent, and not
// kept once the district is found. Each Congress's map is asked on its own, as the address lookup
// asks each Congress's own lines; a Congress where the position is near a line gets no seat, with
// the reason, rather than a guess.

export type Located =
  | { status: 'match'; choices: Choice[]; accuracy: number }
  | { status: 'choose'; choices: Choice[]; accuracy: number }
  | { status: 'none'; accuracy: number }
  | { status: 'water'; state: string; accuracy: number }
  | { status: 'failed'; why: string };

const within = (m: number) => (m < 1000 ? `${Math.max(1, Math.round(m))} m` : `${(m / 1000).toFixed(m < 10000 ? 1 : 0)} km`);
export const accuracyWords = within;

function position(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) { reject(new Error('this browser cannot share a position')); return; }
    navigator.geolocation.getCurrentPosition(resolve, (e) => reject(new Error(
      e.code === 1 ? 'your browser did not share your position'
        : e.code === 3 ? 'your browser took too long to find your position'
          : 'your browser could not find your position just now')),
    { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 });
  });
}

const MAX_CHOICES = 6;

export async function fromPosition(terms: { congress: number; districts?: string }[],
                                   loadMap: (path: string) => Promise<DistrictMap>): Promise<Located> {
  const withMaps = terms.filter((t) => t.districts);
  if (!withMaps.length) return { status: 'failed', why: 'the district map is not published yet' };
  let pos: GeolocationPosition;
  try { pos = await position(); } catch (e) { return { status: 'failed', why: (e as Error).message }; }
  const { longitude: lon, latitude: lat } = pos.coords;
  const accuracy = pos.coords.accuracy ?? 0;
  let maps: { congress: number; map: DistrictMap }[];
  try {
    maps = await Promise.all(withMaps.map(async (t) => ({ congress: t.congress, map: await loadMap(t.districts!) })));
  } catch (e) {
    return { status: 'failed', why: `the district map could not be loaded (${(e as Error).message})` };
  }
  maps.sort((a, b) => b.congress - a.congress);
  const [current, ...older] = maps;
  const here = locate(current.map, lon, lat, accuracy);
  const candidates = [...new Set([...(here.found === null ? [] : [here.found]), ...here.near])];
  if (!candidates.length) return { status: 'none', accuracy };
  if (candidates.length > MAX_CHOICES) {
    return { status: 'failed', why: `your browser placed you only to within ${within(accuracy)}, which touches ${candidates.length} districts` };
  }
  // The older Congresses: a clear answer or none, since the visitor cannot be asked twice about
  // one position and the position is not kept to ask again later.
  const olderSeats: Record<string, string | null> = {};
  const missing: Record<string, string> = {};
  const states = [...new Set(candidates.map((i) => current.map.districts[i].state))];
  for (const { congress, map } of older) {
    if (map.unavailable && states.every((s) => map.unavailable!.includes(s))) {
      olderSeats[String(congress)] = null;
      missing[String(congress)] = unavailableWhy(states[0]);
      continue;
    }
    const r = locate(map, lon, lat, accuracy);
    const clear = r.found !== null && r.near.every((i) => i === r.found);
    olderSeats[String(congress)] = clear ? map.districts[r.found!].seat : null;
    if (!clear) {
      missing[String(congress)] = r.found === null && !r.near.length
        ? 'your position is not in any district on that Congress’s map'
        : `your position was within ${within(current.map.tolerance_m + accuracy)} of a district line on that Congress’s map, so it is not guessed; the address box gives an exact answer`;
    }
  }
  const choices: Choice[] = candidates.map((i) => {
    const d = current.map.districts[i];
    return {
      matched: d.seat ?? d.state,
      state: d.state,
      senators: d.kind !== 'delegate',
      kind: d.kind,
      seats: { [String(current.congress)]: d.seat, ...olderSeats },
      ...(Object.keys(missing).length ? { missing } : {}),
    };
  });
  if (choices.length === 1 && choices[0].kind === 'none') return { status: 'water', state: choices[0].state, accuracy };
  return choices.length === 1 ? { status: 'match', choices, accuracy } : { status: 'choose', choices, accuracy };
}
